/**
 * Metrics Provider Component
 *
 * Initializes the metrics service with the appropriate configuration.
 * Should be placed near the root of your application.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

import { metricsService, MetricsConfig, Platform } from './metricsService';

interface MetricsContextValue {
  isInitialized: boolean;
  isEnabled: boolean;
}

const MetricsContext = createContext<MetricsContextValue>({
  isInitialized: false,
  isEnabled: false,
});

interface MetricsProviderProps {
  children: React.ReactNode;
  /** Base URL for the backend API */
  baseUrl: string;
  /** Platform (web or mobile) */
  platform: Platform;
  /** Environment name */
  environment: string;
  /** Region identifier */
  region: string;
  /** Authentication token for metrics endpoints (METRICS_AUTH_TOKEN) */
  authToken?: string;
  /** Enable/disable metrics (useful for dev environments) */
  enabled?: boolean;
  /** Batch interval in milliseconds */
  batchInterval?: number;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Provider component that initializes the metrics service
 */
export function MetricsProvider({
  children,
  baseUrl,
  platform,
  environment,
  region,
  authToken,
  enabled = true,
  batchInterval = 10000,
  debug = false,
}: MetricsProviderProps): JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const config: MetricsConfig = {
      baseUrl,
      platform,
      environment,
      region,
      authToken,
      enabled,
      batchInterval,
      debug,
    };

    metricsService.initialize(config);
    setIsInitialized(true);

    // Cleanup on unmount
    return () => {
      metricsService.shutdown();
    };
  }, [baseUrl, platform, environment, region, authToken, enabled, batchInterval, debug]);

  return (
    <MetricsContext.Provider value={{ isInitialized, isEnabled: enabled }}>
      {children}
    </MetricsContext.Provider>
  );
}

/**
 * Hook to access the metrics context
 */
export function useMetricsContext(): MetricsContextValue {
  return useContext(MetricsContext);
}

